import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  Table,
  Select,
  Input,
  DatePicker,
  Drawer,
  Button,
  Spin,
  Timeline,
  Card,
  Badge,
  message,
  Tabs,
  Dropdown,
  Menu,
  Skeleton,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SendOutlined,
  UploadOutlined,
  CheckOutlined,
  PaperClipOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import dayjs from "dayjs";
import axiosInstance from "../../../helpers/axios";
import {
  formatToLocalDate,
  useDebounce,
  getAntdSortOrder,
} from "../../../helpers/utils";
import { CreateQuotationStepsEnum } from "../Quotations/quotationTypes";
import {
  getDecryptedName,
  getUserRole,
  decryptByText,
} from "../../../helpers/bycrypt";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import { uploadFile } from "../../../helpers/fileUploader";
import { deleteFromS3 } from "../../../helpers/awsUpload";
import { getTeamMembersListAPI } from "../TeamMembers/teamMembersApis";
import { useAppDispatch } from "../../../store";
import { CommonConfirmationModal } from "../../../Components/CommonConfirmationModal";
import { svg } from "../../../Assets/svg";
import NotificationButton from "../../Components/NotificationButton";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import FullScreenLoader from "../../Components/FullScreenLoader";
import IssueBlockTable from "./IssueBlockTable";
import ClearComponent from "../../Components/ClearComponent";
import { Capacitor } from "@capacitor/core";
import { getCapacitorBaseUrl } from "../../../helpers/common";

const { RangePicker } = DatePicker;

export const QUOTE_REQUEST_SCROLL_KEY = "quote_requests_scroll_top";
export const QUOTE_REQUEST_FILTER_KEY = "quote_requests_filters";
export const QUOTE_REQUEST_REFETCH_KEY = "quote_requests_needs_refetch";

try {
  const navEntries = performance.getEntriesByType("navigation");
  const isReload =
    navEntries.length > 0 &&
    (navEntries[0] as PerformanceNavigationTiming).type === "reload";
  const isLegacyReload =
    performance.navigation && performance.navigation.type === 1;

  if (isReload || isLegacyReload) {
    sessionStorage.removeItem(QUOTE_REQUEST_FILTER_KEY);
    sessionStorage.removeItem(QUOTE_REQUEST_SCROLL_KEY);
  }
} catch (e) {}

const getPersistedFilters = () => {
  try {
    const raw = sessionStorage.getItem(QUOTE_REQUEST_FILTER_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
};

const persistFilters = (filters: Record<string, any>) => {
  sessionStorage.setItem(QUOTE_REQUEST_FILTER_KEY, JSON.stringify(filters));
};

// Status details config
const STATUS_OPTIONS = [
  { label: "To Do", value: "ToDo", classNames: "status-todo" },
  {
    label: "In Progress",
    value: "InProgress",
    classNames: "status-inprogress",
  },
  { label: "On Hold", value: "OnHold", classNames: "status-onhold" },
  { label: "Completed", value: "Completed", classNames: "status-completed" },
  {
    label: "Quote Created",
    value: "Quote Created",
    classNames: "status-quotecreated",
  },
  { label: "Issue", value: "Issue", classNames: "status-issue" },
  { label: "Blocked", value: "Blocked", classNames: "status-blocked" },
  { label: "Approved", value: "Approved", classNames: "status-completed" },
];

export default function QuoteRequests() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userRole = getUserRole() || "";
  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData.QUOTE_REQUESTS,
  );

  const _pf = getPersistedFilters();

  // Filters & State
  const [search, setSearch] = useState(_pf?.search ?? "");
  const debouncedSearch = useDebounce(search, 500);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    _pf?.statusFilter ?? undefined,
  );
  const [assignedFilter, setAssignedFilter] = useState<any>(() => {
    const raw = _pf?.assignedFilter;
    if (raw) {
      if (typeof raw === "string") {
        return { value: raw, label: "" };
      }
      return raw;
    }
    return undefined;
  });
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  >(() => {
    if (_pf?.dateRange && Array.isArray(_pf.dateRange)) {
      return [
        _pf.dateRange[0] ? dayjs(_pf.dateRange[0]) : null,
        _pf.dateRange[1] ? dayjs(_pf.dateRange[1]) : null,
      ];
    }
    return null;
  });
  const [sorterState, setSorterState] = useState<{
    field: string | null;
    order: "asc" | "desc" | null;
  }>(_pf?.sorterState ?? { field: null, order: null });
  const [archiveFilter, setArchiveFilter] = useState<boolean>(
    _pf?.archiveFilter ?? false,
  );
  const [isFilterOpen, setIsFilterOpen] = useState(_pf?.isFilterOpen ?? false);

  // Delete Quote Request State
  const [deleteRequestId, setDeleteRequestId] = useState<string | null>(null);
  const [deleteRequestLoading, setDeleteRequestLoading] = useState(false);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    if (sorter.order) {
      const order = sorter.order === "ascend" ? "asc" : "desc";
      setSorterState({ field: sorter.columnKey, order });
    } else {
      setSorterState({ field: null, order: null });
    }
  };

  // Pagination & List
  const queryClient = useQueryClient();

  const queryFilters = {
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    assigned_person: assignedFilter ? assignedFilter.value : undefined,
    start_date:
      dateRange && dateRange[0]
        ? dateRange[0].startOf("day").toISOString()
        : undefined,
    end_date:
      dateRange && dateRange[1]
        ? dateRange[1].endOf("day").toISOString()
        : undefined,
    sortBy: sorterState.field || undefined,
    sortOrder: sorterState.order || undefined,
    is_archived: archiveFilter,
  };

  const {
    data: queryData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ["quoteRequests", queryFilters],
    queryFn: async ({ pageParam = 1, signal }) => {
      const response = await axiosInstance.get("/quote-requests", {
        params: {
          page: pageParam,
          limit: 25,
          ...queryFilters,
        },
        signal,
      });
      if (response.data?.success) {
        return response.data.data;
      }
      throw new Error("Failed to fetch design requests");
    },
    getNextPageParam: (lastPage: any, allPages) => {
      const fetched = allPages.reduce(
        (acc: number, p: any) => acc + (p?.requests?.length || 0),
        0,
      );
      const total = lastPage?.total || 0;
      return fetched < total ? allPages.length + 1 : undefined;
    },
    staleTime: 0,
    initialPageParam: 1,
  });

  const quoteRequests: any[] = queryData
    ? queryData.pages.flatMap((p: any) => p?.requests || [])
    : [];
  const totalCount: number = queryData?.pages?.[0]?.total || 0;
  const scrollableDivId = "quoteRequestsScrollDiv";

  const [isMounting, setIsMounting] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setIsMounting(false), 20);
    return () => clearTimeout(timer);
  }, []);

  const scrollableDivRef = useRef<HTMLDivElement | null>(null);
  const scrollRestoredRef = useRef(false);

  const saveScrollPosition = () => {
    const parent = scrollableDivRef.current;
    const target =
      parent?.querySelector(".infinite-scroll-component") || parent;
    if (target) {
      sessionStorage.setItem(
        QUOTE_REQUEST_SCROLL_KEY,
        String(target.scrollTop),
      );
    }
  };

  useLayoutEffect(() => {
    if (
      !scrollRestoredRef.current &&
      quoteRequests.length > 0 &&
      !isLoading &&
      !isMounting
    ) {
      const savedScroll = sessionStorage.getItem(QUOTE_REQUEST_SCROLL_KEY);
      if (savedScroll) {
        const parent = scrollableDivRef.current;
        const target =
          parent?.querySelector(".infinite-scroll-component") || parent;
        if (target) {
          target.scrollTop = parseInt(savedScroll, 10);
          requestAnimationFrame(() => {
            setTimeout(() => {
              target.dispatchEvent(new Event("scroll", { bubbles: true }));
            }, 100);
          });
        }
        sessionStorage.removeItem(QUOTE_REQUEST_SCROLL_KEY);
      }
      scrollRestoredRef.current = true;
    }
  }, [quoteRequests, isLoading, isMounting]);

  const isFirstMountForFilters = useRef(true);
  useEffect(() => {
    if (isFirstMountForFilters.current) {
      isFirstMountForFilters.current = false;
      return;
    }
    scrollRestoredRef.current = false;
    if (scrollableDivRef.current) {
      scrollableDivRef.current.scrollTop = 0;
    }
    queryClient.removeQueries({
      queryKey: ["quoteRequests"],
      type: "inactive",
    });
  }, [
    debouncedSearch,
    statusFilter,
    assignedFilter?.value,
    dateRange,
    sorterState,
    archiveFilter,
  ]);

  useEffect(() => {
    persistFilters({
      search,
      statusFilter,
      assignedFilter,
      dateRange: dateRange
        ? [
            dateRange[0] ? dateRange[0].startOf("day").toISOString() : null,
            dateRange[1] ? dateRange[1].endOf("day").toISOString() : null,
          ]
        : null,
      sorterState,
      archiveFilter,
      isFilterOpen,
    });
  }, [
    search,
    statusFilter,
    assignedFilter,
    dateRange,
    sorterState,
    archiveFilter,
    isFilterOpen,
  ]);

  useEffect(() => {
    if (sessionStorage.getItem(QUOTE_REQUEST_REFETCH_KEY) === "1") {
      sessionStorage.removeItem(QUOTE_REQUEST_REFETCH_KEY);
      queryClient.removeQueries({ queryKey: ["quoteRequests"] });
    }
  }, []);

  // Stats Widgets
  const [stats, setStats] = useState<{
    total: number;
    active: number;
    openIssues: number;
    convertedCount: number;
    conversionRate: number;
    breakdown: any;
    statusList: { status: string; label: string; count: number | string }[];
  }>({
    total: 0,
    active: 0,
    openIssues: 0,
    convertedCount: 0,
    conversionRate: 0,
    breakdown: {},
    statusList: [],
  });

  const [statsLoading, setStatsLoading] = useState(true);

  // Modal / Drawer States
  const [isMobile, setIsMobile] = useState(window.innerWidth < 991.98);
  const [designTeamMembers, setDesignTeamMembers] = useState<any[]>([]);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [activeDetailsTab, setActiveDetailsTab] = useState("overview");

  // Client Changes CRUD states
  const [newClientChangeText, setNewClientChangeText] = useState("");
  const [editingClientChangeId, setEditingClientChangeId] = useState<
    string | null
  >(null);
  const [editingClientChangeText, setEditingClientChangeText] = useState("");

  // Issues CRUD states
  const [newIssueContent, setNewIssueContent] = useState("");
  const [newIssueDescription, setNewIssueDescription] = useState("");
  const [editingIssueId, setEditingIssueId] = useState<string | null>(null);
  const [editingIssueContent, setEditingIssueContent] = useState("");
  const [editingIssueDescription, setEditingIssueDescription] = useState("");

  // Uploading / converting states
  const [uploadingDesign, setUploadingDesign] = useState(false);
  const [convertingRequest, setConvertingRequest] = useState(false);

  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [selectedArchiveRequest, setSelectedArchiveRequest] =
    useState<any>(null);
  const [archiveLoading, setArchiveLoading] = useState(false);

  const [confirmConvertOpen, setConfirmConvertOpen] = useState(false);
  const [unresolvedWarningOpen, setUnresolvedWarningOpen] = useState(false);
  const [unresolvedWarningText, setUnresolvedWarningText] = useState("");

  const handleToggleArchive = async () => {
    if (!selectedArchiveRequest) return;

    setArchiveLoading(true);

    try {
      const res = await axiosInstance.put(
        `/quote-requests/${selectedArchiveRequest._id}/archive`,
      );

      if (res.data?.success) {
        message.success(
          selectedArchiveRequest.is_archived
            ? "Design Request unarchived successfully."
            : "Design Request archived successfully.",
        );

        queryClient.invalidateQueries({
          queryKey: ["quoteRequests"],
        });

        fetchStats();

        setArchiveModalOpen(false);
        setSelectedArchiveRequest(null);
      }
    } catch (err: any) {
      message.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setArchiveLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await axiosInstance.get("/quote-requests/stats");
      if (response.data?.success) {
        const data = response.data.data;
        // Use the pre-calculated breakdown from backend
        const bd = data.breakdown || {};

        setStats({
          total: data.total || 0,
          active: bd.active || 0,
          openIssues: bd.openIssues || 0,
          convertedCount: data.converted || 0,
          conversionRate: data.conversionRate || 0,
          breakdown: data.breakdown || {},
          statusList: data.statusList || [],
        });
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 991.98);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    dispatch(
      getTeamMembersListAPI({
        filters: { roleKey: "design-team", limit: 100 },
        onSuccess: (res: any) => {
          setDesignTeamMembers(res?.data || []);
        },
      }),
    );
  }, [dispatch]);

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleShareApprovalLink = (id: string) => {
    const basePath = window.location.pathname.split("/quote-requests")[0]; // usually "/sm-crm" or ""
    let publicUrl = `${window.location.origin}${basePath}/public/quote-request/${id}`;
    if (Capacitor.isNativePlatform()) {
      publicUrl = getCapacitorBaseUrl(publicUrl);
    }

    try {
      const textArea = document.createElement("textarea");
      textArea.value = publicUrl;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      textArea.remove();
      message.success("Approval link copied to clipboard!");
    } catch (err) {
      console.error("Clipboard copy failed", err);
      message.error("Failed to copy link");
    }
  };

  const handleDeleteQuoteRequest = async () => {
    if (!deleteRequestId) return;
    setDeleteRequestLoading(true);
    try {
      const response = await axiosInstance.delete(
        `/quote-requests/${deleteRequestId}`,
      );
      if (response.data?.success) {
        // Remove files from S3 bucket
        const filesRemoved = response.data?.data?.filesRemoved || [];
        if (filesRemoved.length > 0) {
          for (const fileUrl of filesRemoved) {
            try {
              if (!fileUrl) continue;
              let key = fileUrl;
              if (
                fileUrl.startsWith("http://") ||
                fileUrl.startsWith("https://")
              ) {
                const urlObj = new URL(fileUrl);
                key = urlObj.pathname.substring(1);
              } else if (key.startsWith("/")) {
                key = key.substring(1);
              }
              if (key) {
                await deleteFromS3(key);
                const fileName = key.split("/").pop();
                if (fileName) {
                  await deleteFromS3(`thumbnails/${fileName}`).catch(() => {});
                }
              }
            } catch (s3Err) {
              console.error("Failed to delete S3 object:", fileUrl, s3Err);
            }
          }
        }

        message.success(
          response.data.message ||
            "Design Request and all associated files deleted successfully",
        );
        queryClient.invalidateQueries({ queryKey: ["quoteRequests"] });
        fetchStats();
        setDeleteRequestId(null);
      } else {
        message.error(
          response.data?.message || "Failed to delete design request",
        );
      }
    } catch (err: any) {
      console.error(err);
      message.error(
        err.response?.data?.message || "Failed to delete design request",
      );
    } finally {
      setDeleteRequestLoading(false);
    }
  };

  const handleSendToClient = async (id: string) => {
    try {
      message.loading({
        content: "Sending email to client...",
        key: "send-client",
      });

      const res = await axiosInstance.post(
        `/quote-requests/${id}/send-to-client`,
      );

      if (res.data.success) {
        if (res.data.data?.publicUrl) {
          try {
            const textArea = document.createElement("textarea");
            textArea.value = res.data.data.publicUrl;
            textArea.style.position = "fixed";
            textArea.style.left = "-999999px";
            textArea.style.top = "-999999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand("copy");
            textArea.remove();
          } catch (err) {
            console.error(err);
          }
        }

        message.success({
          content: "Email sent and link copied to clipboard!",
          key: "send-client",
        });
      }
    } catch (err: any) {
      message.error({
        content: err.response?.data?.message || "Failed to send email",
        key: "send-client",
      });
    }
  };

  // Open Details Drawer
  const handleOpenDetails = async (id: string) => {
    setDetailsLoading(true);
    setIsDetailsOpen(true);
    setActiveDetailsTab("overview");
    try {
      const resDetails = await axiosInstance.get(`/quote-requests/${id}`);
      if (resDetails.data?.success) {
        const reqData = resDetails.data.data;
        const selectedChangeId = reqData?.selected_change?._id;
        const [resStatusLogs, resHistoryLogs] = await Promise.all([
          axiosInstance
            .get(
              `/quote-requests/${id}/status-logs${
                selectedChangeId
                  ? `?change=${encodeURIComponent(selectedChangeId)}`
                  : ""
              }`,
            )
            .catch(() => null),
          axiosInstance
            .get(`/quote-requests/${id}/history-logs`)
            .catch(() => null),
        ]);
        setSelectedRequest({
          ...reqData,
          status_logs: resStatusLogs?.data?.data || [],
          history_logs: resHistoryLogs?.data?.data || [],
        });
      }
    } catch (err) {
      console.error("Error loading details:", err);
      message.error("Failed to load details");
      setIsDetailsOpen(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Refresh selected request details
  const refreshDetails = async (id: string) => {
    try {
      const resDetails = await axiosInstance.get(`/quote-requests/${id}`);
      if (resDetails.data?.success) {
        const reqData = resDetails.data.data;
        const selectedChangeId = reqData?.selected_change?._id;
        const [resStatusLogs, resHistoryLogs] = await Promise.all([
          axiosInstance
            .get(
              `/quote-requests/${id}/status-logs${
                selectedChangeId
                  ? `?change=${encodeURIComponent(selectedChangeId)}`
                  : ""
              }`,
            )
            .catch(() => null),
          axiosInstance
            .get(`/quote-requests/${id}/history-logs`)
            .catch(() => null),
        ]);
        const enrichedRequest = {
          ...reqData,
          status_logs: resStatusLogs?.data?.data || [],
          history_logs: resHistoryLogs?.data?.data || [],
        };
        setSelectedRequest(enrichedRequest);
        // update request in the list cache
        queryClient.setQueryData(
          ["quoteRequests", queryFilters],
          (old: any) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                requests: (page.requests || []).map((req: any) =>
                  req._id === id ? { ...req, ...resDetails.data.data } : req,
                ),
              })),
            };
          },
        );
      }
    } catch (err) {
      console.error("Error refreshing details:", err);
    }
  };

  // Change Status
  const handleChangeStatus = async (newStatus: string) => {
    if (!selectedRequest) return;
    try {
      const changeId =
        selectedRequest.selected_change?._id ||
        (typeof selectedRequest.selected_change === "string"
          ? selectedRequest.selected_change
          : undefined) ||
        selectedRequest.change_id ||
        selectedRequest._id;

      const response = await axiosInstance.put(
        `/quote-requests/${selectedRequest._id}/status`,
        {
          status: newStatus,
          change_id: changeId,
        },
      );
      if (response.data?.success) {
        message.success(`Status updated to ${newStatus}`);
        refreshDetails(selectedRequest._id);
        fetchStats();
      }
    } catch (err: any) {
      message.error(err.response?.data?.message || "Failed to update status");
    }
  };

  // Client Changes CRUD
  const handleAddClientChange = async () => {
    if (!newClientChangeText.trim() || !selectedRequest) return;
    try {
      const response = await axiosInstance.post(
        `/quote-requests/${selectedRequest._id}/client-changes`,
        {
          content: newClientChangeText,
        },
      );
      if (response.data?.success) {
        message.success("Client change added");
        setNewClientChangeText("");
        refreshDetails(selectedRequest._id);
      }
    } catch (err: any) {
      message.error(
        err.response?.data?.message || "Failed to add client change",
      );
    }
  };

  const handleStartEditClientChange = (change: any) => {
    setEditingClientChangeId(change._id);
    setEditingClientChangeText(change.content);
  };

  const showLockedToast = () => {
    message.info({
      content: (
        <span>
          This quote request has already been converted to{" "}
          <strong>Quote Created</strong>. So, cannot be edited.
        </span>
      ),
      key: "status_lock_redirect_toast",
    });
  };

  const handleSaveClientChange = async (changeId: string) => {
    if (!editingClientChangeText.trim() || !selectedRequest) return;
    try {
      const response = await axiosInstance.put(
        `/quote-requests/${selectedRequest._id}/client-changes/${changeId}`,
        {
          content: editingClientChangeText,
        },
      );
      if (response.data?.success) {
        message.success("Client change updated");
        setEditingClientChangeId(null);
        refreshDetails(selectedRequest._id);
      }
    } catch (err: any) {
      if (err?.response?.status === 417) {
        setEditingClientChangeId(null);
        showLockedToast();
        if (selectedRequest) refreshDetails(selectedRequest._id);
        return;
      }
      message.error(
        err.response?.data?.message || "Failed to update client change",
      );
    }
  };

  const handleDeleteClientChange = async (changeId: string) => {
    if (!selectedRequest) return;
    try {
      const response = await axiosInstance.delete(
        `/quote-requests/${selectedRequest._id}/client-changes/${changeId}`,
      );
      if (response.data?.success) {
        message.success("Client change removed");
        refreshDetails(selectedRequest._id);
      }
    } catch (err: any) {
      if (err?.response?.status === 417) {
        showLockedToast();
        if (selectedRequest) refreshDetails(selectedRequest._id);
        return;
      }
      message.error(
        err.response?.data?.message || "Failed to delete client change",
      );
    }
  };

  // Issues CRUD
  const handleAddIssue = async () => {
    if (!newIssueContent.trim() || !selectedRequest) return;
    try {
      const response = await axiosInstance.post(
        `/quote-requests/${selectedRequest._id}/issues`,
        {
          content: newIssueContent,
          description: newIssueDescription,
        },
      );
      if (response.data?.success) {
        message.success("Issue added");
        setNewIssueContent("");
        setNewIssueDescription("");
        refreshDetails(selectedRequest._id);
        fetchStats();
      }
    } catch (err: any) {
      if (err?.response?.status === 417) {
        showLockedToast();
        if (selectedRequest) refreshDetails(selectedRequest._id);
        return;
      }
      message.error(err.response?.data?.message || "Failed to add issue");
    }
  };

  const handleStartEditIssue = (issue: any) => {
    setEditingIssueId(issue._id);
    setEditingIssueContent(issue.content);
    setEditingIssueDescription(issue.description || "");
  };

  const handleSaveIssue = async (issueId: string) => {
    if (!editingIssueContent.trim() || !selectedRequest) return;
    try {
      const response = await axiosInstance.put(
        `/quote-requests/${selectedRequest._id}/issues/${issueId}`,
        {
          content: editingIssueContent,
          description: editingIssueDescription,
        },
      );
      if (response.data?.success) {
        message.success("Issue updated");
        setEditingIssueId(null);
        refreshDetails(selectedRequest._id);
      }
    } catch (err: any) {
      if (err?.response?.status === 417) {
        setEditingIssueId(null);
        showLockedToast();
        if (selectedRequest) refreshDetails(selectedRequest._id);
        return;
      }
      message.error(err.response?.data?.message || "Failed to update issue");
    }
  };

  const handleDeleteIssue = async (issueId: string) => {
    if (!selectedRequest) return;
    try {
      const response = await axiosInstance.delete(
        `/quote-requests/${selectedRequest._id}/issues/${issueId}`,
      );
      if (response.data?.success) {
        message.success("Issue removed");
        refreshDetails(selectedRequest._id);
        fetchStats();
      }
    } catch (err: any) {
      if (err?.response?.status === 417) {
        showLockedToast();
        if (selectedRequest) refreshDetails(selectedRequest._id);
        return;
      }
      message.error(err.response?.data?.message || "Failed to delete issue");
    }
  };

  // Design Upload & Send
  const handleDesignUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0 || !selectedRequest) return;
    const file = fileList[0];
    setUploadingDesign(true);
    try {
      const uploadRes = await uploadFile(file, { folder: "designs" });
      if (uploadRes.success) {
        const changeId =
          selectedRequest.selected_change?._id ||
          (typeof selectedRequest.selected_change === "string"
            ? selectedRequest.selected_change
            : undefined) ||
          selectedRequest.change_id ||
          selectedRequest._id;

        const payload = {
          designs: [
            {
              file_name: file.name,
              file_url: uploadRes.url,
            },
          ],
          client_change_id: changeId,
        };

        const response = await axiosInstance.post(
          `/quote-requests/${selectedRequest._id}/client-changes/${changeId}/designs`,
          payload,
        );
        if (response.data?.success) {
          message.success("Design uploaded successfully");
          refreshDetails(selectedRequest._id);
        }
      } else {
        message.error(uploadRes.message || "Failed to upload file");
      }
    } catch (err: any) {
      if (err?.response?.status === 417) {
        showLockedToast();
        if (selectedRequest) refreshDetails(selectedRequest._id);
        return;
      }
      console.error("Design upload error:", err);
      message.error("Error uploading file");
    } finally {
      setUploadingDesign(false);
    }
  };

  const handleSendDesigns = async () => {
    if (!selectedRequest) return;
    try {
      const changeId =
        selectedRequest.selected_change?._id ||
        (typeof selectedRequest.selected_change === "string"
          ? selectedRequest.selected_change
          : undefined) ||
        selectedRequest.change_id ||
        selectedRequest._id;

      const response = await axiosInstance.post(
        `/quote-requests/${selectedRequest._id}/client-changes/${changeId}/send-designs`,
      );
      if (response.data?.success) {
        message.success("Designs sent to client successfully");
        refreshDetails(selectedRequest._id);
      }
    } catch (err: any) {
      message.error(err.response?.data?.message || "Failed to send designs");
    }
  };

  // Convert to Quote
  const handleConvertToQuote = async () => {
    if (!selectedRequest) return;

    // Check for unresolved issues / blocks
    const currentIssues =
      selectedRequest?.selected_change?.issues || selectedRequest?.issues || [];
    const unresolved = currentIssues.filter(
      (i: any) => !i.is_deleted && i.status !== "Resolved",
    );

    if (unresolved.length > 0) {
      const issueLabels = unresolved
        .map((i: any) => (i.issue_no ? `#${i.issue_no}` : `"${i.content}"`))
        .join(", ");
      setUnresolvedWarningText(
        `Cannot convert to Quote because unresolved issue(s)/block(s) exist: ${issueLabels}. Please resolve all issues/blocks first before converting to Quote.`,
      );
      setUnresolvedWarningOpen(true);
      return;
    }

    setConfirmConvertOpen(true);
  };

  const handleConfirmConvert = async () => {
    if (!selectedRequest) return;
    setConvertingRequest(true);
    try {
      const response = await axiosInstance.post(
        `/quote-requests/${selectedRequest._id}/convert`,
      );
      if (response.data?.success || response.data?.status) {
        message.success("Converted to Quote successfully!");
        refreshDetails(selectedRequest._id);
        fetchStats();
        setConfirmConvertOpen(false);
        const resData = response.data?.data;
        const quoteId =
          resData?.quote_id || resData?.quote?._id || resData?._id;
        if (quoteId) {
          navigate(
            `/quotations/create-quotation?step=${CreateQuotationStepsEnum.CUSTOMER_DETAILS}&quotationId=${quoteId}&mode=edit`,
          );
        }
      }
    } catch (err: any) {
      message.error(
        err.response?.data?.message || "Failed to convert request to quote",
      );
    } finally {
      setConvertingRequest(false);
    }
  };

  // Helpers to get durations & counters
  const getDurationString = (ms: number) => {
    if (!ms) return "0s";
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const MoreOptionsMenu = (data: any) => {
    return (
      <Menu>
        <Menu.Item
          key="1"
          onClick={() => {
            navigate(`/quote-requests/${data?._id}`);
          }}
        >
          <div className="text-black d-flex align-items-center gap-2 svg-20">
            View
          </div>
        </Menu.Item>
        {USER_PERMISSIONS.MODIFY && (
          <Menu.Item
            key="archive"
            onClick={() => {
              setArchiveModalOpen(true);
              setSelectedArchiveRequest(data);
            }}
          >
            <div className="text-black d-flex align-items-center gap-2 svg-20">
              {data.is_archived ? "Unarchive" : "Archive"}
            </div>
          </Menu.Item>
        )}
        {USER_PERMISSIONS.MODIFY &&
          data.selected_change?.status !== "Quote Created" &&
          data.selected_change?.designs &&
          data.selected_change?.designs.length > 0 && (
            <>
              <Menu.Item
                key="send-to-client"
                onClick={() => handleSendToClient(data._id)}
              >
                <div className="text-black d-flex align-items-center gap-2 svg-20">
                  Send to Client
                </div>
              </Menu.Item>
              {data?.selected_change?.status !== "Approved" && (
                <Menu.Item
                  key="share-link"
                  onClick={() => handleShareApprovalLink(data._id)}
                >
                  <div className="text-black d-flex align-items-center gap-2 svg-20">
                    Share Approval Link
                  </div>
                </Menu.Item>
              )}
            </>
          )}
        {/* {USER_PERMISSIONS.REMOVE && data.status !== "Quote Created" && (
          <Menu.Item
            key="delete"
            onClick={() => {
              setDeleteRequestId(data._id);
            }}
          >
            <div className="text-danger d-flex align-items-center gap-2 svg-20">
              Remove
            </div>
          </Menu.Item>
        )} */}
      </Menu>
    );
  };

  // Columns definition
  const columns = [
    {
      title: "Request ID",
      dataIndex: "display_quote_request_id",
      key: "display_quote_request_id",
      sorter: true,
      sortOrder: getAntdSortOrder("display_quote_request_id", sorterState),
      render: (text: string) => (
        <span className="fw-bold text-primary">{text}</span>
      ),
    },
    {
      title: "Client",
      key: "customer_snapshot.first_name",
      sorter: true,
      sortOrder: getAntdSortOrder("customer_snapshot.first_name", sorterState),
      render: (record: any) => {
        const snap = record.customer_snapshot || {};
        return (
          <div className="d-flex flex-column">
            <span className="fw-semibold text-dark">
              {snap.first_name} {snap.last_name}
            </span>
            <span className="text-muted small">{snap.company_name || "-"}</span>
          </div>
        );
      },
    },
    {
      title: "Assigned",
      dataIndex: "assigned_team_members",
      key: "assigned",
      render: (members: any[]) => {
        if (!members || members.length === 0)
          return <span className="text-muted">Unassigned</span>;
        const mambers = members.map((m) => {
          return getDecryptedName(m);
        });
        return <span className="">{mambers.join(", ")}</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      sortOrder: getAntdSortOrder("status", sorterState),
      render: (status: string) => {
        const { classNames, label } = STATUS_OPTIONS.find(
          (s) => s.value === status,
        ) || { label: status, className: "status-default" };
        return (
          // <Badge
          //   count={config.label}
          //   style={{
          //     // backgroundColor: config.bg,
          //     // color: config.color,
          //     // borderColor: config.color,
          //     // fontWeight: 600,
          //     // fontSize: "12px",
          //     // padding: "0 10px",
          //     // borderRadius: "4px"
          //   }}
          // />
          <div className="d-flex flex-column align-items-start gap-1">
            <div className="d-flex align-items-center gap-2">
              <span
                className={
                  "d-inline-flex align-items-center billStatus " + classNames
                }
              >
                {svg.dotIcon}
                {label}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Time Active",
      key: "time_active",
      sorter: true,
      sortOrder: getAntdSortOrder("time_active", sorterState),
      render: (record: any) => {
        const selChange = record.selected_change;
        if (
          selChange &&
          (selChange.time_active ||
            selChange.active_time_formatted ||
            selChange.active_time_ms !== undefined)
        ) {
          return (
            <span>
              {selChange.time_active ||
                selChange.active_time_formatted ||
                getDurationString(selChange.active_time_ms)}
            </span>
          );
        }
        const start = new Date(record.created_at).getTime();
        const end = record.design_approved_at
          ? new Date(record.design_approved_at).getTime()
          : new Date().getTime();
        const totalMs = Math.max(0, end - start);
        return <span>{getDurationString(totalMs)}</span>;
      },
    },
    {
      title: "Issues",
      key: "issues_count",
      sorter: true,
      sortOrder: getAntdSortOrder("issues_count", sorterState),
      render: (_: any, record: any) => {
        const issuesList =
          record?.selected_change?.issues || record?.issues || [];
        const activeIssues = issuesList.filter(
          (i: any) => !i.is_deleted && i.status !== "Resolved",
        ).length;
        return activeIssues > 0 ? (
          <Badge
            count={activeIssues}
            style={{ backgroundColor: "#fff0f0", color: "#ff0004" }}
          />
        ) : (
          <span className="text-muted">-</span>
        );
      },
    },
    {
      title: "Created On",
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      sortOrder: getAntdSortOrder("created_at", sorterState),
      render: (date: string) => formatToLocalDate(date, true),
    },
    {
      title: "Action",
      key: "action",
      width: 70,
      render: (record: any) => (
        // <Button type="link" className="p-0 text-primary" onClick={() => handleOpenDetails(record._id)}>
        //   Open ↗
        // </Button>
        <div
          className="d-flex justify-content-center"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            overlay={() => MoreOptionsMenu(record)}
            trigger={["click"]}
            placement="bottomRight"
            className="cursor-pointer"
            overlayClassName="moreOptionDropdown"
          >
            <span className="svg-28 svg-outline-primary">
              {svg?.tableMoreOption}
            </span>
          </Dropdown>
        </div>
      ),
    },
  ];

  const getFilterCount = () => {
    let count = 0;
    if (statusFilter) count++;
    if (assignedFilter) count++;
    if (dateRange && dateRange[0] && dateRange[1]) count++;
    return count;
  };

  return (
    <div className={`page-content h-100 px-40 py-40 ${isMobile ? "pb-0" : ""}`}>
      <>
        <div className="mb-md-3 mb-2 d-flex flex-column gap-md-2 gap-0">
          {/* Header */}
          <div className="d-flex flex-md-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-center">
            <h2 className="text-black fw-bolder page-title mb-0 order-md-1 order-1 d-flex align-items-center gap-2 text-nowrap">
              Design Requests{" "}
              <span>
                {totalCount !== undefined && totalCount !== null
                  ? `(${totalCount})`
                  : ""}
              </span>
            </h2>
            {/* <div
              className={`d-flex flex-md-nowrap flex-wrap gap-sm-3 gap-2 justify-content-end align-items-center ms-auto order-md-2 order-3 mobileFilter ${
                isFilterOpen ? "is-open" : ""
              }`}
            >
              
            </div> */}
            <div className="d-flex justify-content-between justify-content-md-end gap-md-3 gap-2 order-md-2 order-3 ms-md-auto flex-fill">
              <div className="pageSearch dr_pageSearch mw-100 position-relative d-inline-block">
                <input
                  type="text"
                  value={search}
                  className="form-control form-control-lg"
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search"
                />
                {search?.length ? (
                  <span
                    className="cursor-pointer"
                    onClick={() => setSearch("")}
                  >
                    {svg?.searchClear}
                  </span>
                ) : (
                  <span>{svg?.search}</span>
                )}
              </div>

              {(["admin", "sub-admin"].includes(userRole) ||
                USER_PERMISSIONS.ADD) && (
                <button
                  className="btn btn-lg btn-primary mob-btn flex-shrink-0 px-sm-4 px-2"
                  onClick={() => navigate("/quote-requests/create")}
                >
                  + CREATE REQUEST
                </button>
              )}
            </div>

            <div className="d-flex gap-md-3 gap-2 order-md-3 order-2">
              <div
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                role="button"
                aria-expanded={isFilterOpen}
                className="filterButton position-relative ms-sm-0 ms-auto"
              >
                {svg?.filterIcon}
                {getFilterCount() > 0 && (
                  <span className="position-absolute badge customBadgeFont rounded-pill bg-danger">
                    {getFilterCount()}
                  </span>
                )}
              </div>
              <NotificationButton />
            </div>
          </div>

          {/* Filter Slide Row */}
          <div
            className={`filter-transition-container overflow-hidden ${
              isFilterOpen ? "is-open" : ""
            }`}
          >
            <div className="d-flex flex-wrap align-items-end bg-white p-2 rounded-3 mt-2 position-relative">
              <label className="filterTitle fw-bold fs-5 position-absolute d-flex align-items-center">
                Filters
              </label>
              <div className="row gx-md-3 gx-2 gy-sm-3 gy-2 w-100">
                <div className="col-lg-auto col-md-4 col-6">
                  <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                    Status
                  </label>
                  <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                    <Select
                      placeholder="Filter by Status"
                      allowClear
                      // style={{ width: "200px" }}
                      value={statusFilter}
                      className="form-select form-select-lg"
                      onChange={setStatusFilter}
                      onClear={() => setStatusFilter(undefined)}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <Select.Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className="col-lg-auto col-md-4 col-6">
                  <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                    Archive
                  </label>

                  <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                    <Select
                      // style={{ width: "180px" }}
                      value={archiveFilter}
                      onChange={setArchiveFilter}
                      className="form-select form-select-lg"
                    >
                      <Select.Option value={false}>Unarchived</Select.Option>

                      <Select.Option value={true}>Archived</Select.Option>
                    </Select>
                  </div>
                </div>
                <div className="col-lg-auto col-md-4 col-sm-6">
                  <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                    Assigned To
                  </label>
                  <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                    <Select
                      labelInValue
                      placeholder="Filter Assigned"
                      allowClear
                      // style={{ width: "200px" }}
                      value={assignedFilter}
                      className="form-select form-select-lg"
                      onChange={(value) => setAssignedFilter(value)}
                      onClear={() => setAssignedFilter(undefined)}
                    >
                      {designTeamMembers.map((member) => {
                        const decryptedName = getDecryptedName(member);
                        const decryptedEmail = member.email
                          ? decryptByText(member.email)
                          : "";
                        const displayName =
                          decryptedName.trim() || decryptedEmail || "Unknown";
                        return (
                          <Select.Option key={member._id} value={member._id}>
                            {displayName}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </div>
                </div>

                <div className="col-lg-auto col-md-4 col-sm-6">
                  <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                    Date Range
                  </label>
                  <div
                    className={`dateSelectFilter RangeDatePicker border-s mw-100 d-inline-block mb-sm-0 mb-2 ${
                      dateRange && dateRange[0] && dateRange[1]
                        ? "is-active"
                        : "not-active"
                    } p-0`}
                    style={{ width: isMobile ? "100%" : "280px" }}
                  >
                    <RangePicker
                      bordered={false}
                      className="antdRangePicker ff"
                      placeholder={["Start Date", "End Date"]}
                      style={{ width: "100%", height: "100%" }}
                      value={dateRange}
                      onChange={(dates) =>
                        setDateRange(dates ? [dates[0], dates[1]] : null)
                      }
                      inputReadOnly={Capacitor.isNativePlatform()}
                    />
                  </div>
                </div>
                {(statusFilter ||
                  assignedFilter ||
                  (dateRange && dateRange[0] && dateRange[1]) ||
                  search) && (
                  <ClearComponent
                    handleClick={() => {
                      setStatusFilter(undefined);
                      setAssignedFilter(undefined);
                      setDateRange(null);
                      setSearch("");
                      setIsFilterOpen(false);
                      setArchiveFilter(false);
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Status Cards - one per status from statusList, matching Dashboard Quotations Overview style */}
          <div className="mt-3">
            <div className="row g-3">
              {(statsLoading || stats.statusList.length === 0
                ? [
                    { status: "ToDo", label: "To Do Requests", count: 0 },
                    {
                      status: "InProgress",
                      label: "In Progress Requests",
                      count: 0,
                    },
                    { status: "OnHold", label: "On Hold Requests", count: 0 },
                    {
                      status: "Completed",
                      label: "Completed Requests",
                      count: 0,
                    },
                    {
                      status: "Quote Created",
                      label: "Quote Created Requests",
                      count: 0,
                    },
                    {
                      status: "Approved",
                      label: "Approved Requests",
                      count: 0,
                    },
                    {
                      status: "issue/block",
                      label: "Open Issue / Block",
                      count: 0,
                    },
                    {
                      status: "avg_completion",
                      label: "Avg. Completion Time",
                      count: "0 mins",
                    },
                  ]
                : stats.statusList
              ).map((item) => {
                const colorMap: Record<string, string> = {
                  ToDo: "#595959",
                  InProgress: "#1890ff",
                  OnHold: "#fa8c16",
                  Completed: "#52c41a",
                  "Quote Created": "#722ed1",
                  Approved: "#52c41a",
                  "issue/block": "#f5222d",
                  avg_completion: "#d0bc00",
                };
                const color = colorMap[item.status] ?? "#595959";
                const isClickable = item.status !== "avg_completion";
                return (
                  <div
                    key={item.status}
                    className="col-6 col-md-3 col-xl-auto"
                    style={{ flex: "1 1 150px", minWidth: "140px" }}
                  >
                    <div
                      className={`card border-0 shadow-sm rounded-3 h-100 ${
                        isClickable && !statsLoading ? "cursor-pointer" : ""
                      }`}
                      style={{ transition: "box-shadow 0.2s" }}
                      onMouseEnter={(e) => {
                        if (isClickable && !statsLoading) {
                          e.currentTarget.style.boxShadow =
                            "0 4px 16px rgba(0,0,0,0.12)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (isClickable && !statsLoading) {
                          e.currentTarget.style.boxShadow = "";
                        }
                      }}
                      onClick={() => {
                        if (!isClickable || statsLoading) return;
                        setStatusFilter(
                          statusFilter === item.status
                            ? undefined
                            : item.status,
                        );
                      }}
                    >
                      <div
                        className="card-body p-3"
                        style={{ minHeight: "80px" }}
                      >
                        <div
                          className="fw-semibold mb-2"
                          style={{
                            fontSize: "12px",
                            color,
                            letterSpacing: "0.2px",
                          }}
                        >
                          {item.label}
                        </div>
                        <div
                          className="fw-bold d-flex align-items-center justify-content-between"
                          style={{
                            fontSize: "28px",
                            lineHeight: 1,
                            color: "#1d1d1d",
                          }}
                        >
                          <div>
                            {statsLoading ? (
                              <Skeleton.Button
                                active
                                size="small"
                                style={{ width: 40, height: 28 }}
                              />
                            ) : (
                              item.count
                            )}
                          </div>
                          {item.status === "issue/block" && (
                            <div
                              className="text-secondary d-flex flex-column text-end"
                              style={{
                                fontSize: "11px",
                                fontWeight: 500,
                                lineHeight: 1.3,
                              }}
                            >
                              <div>
                                <span>Total Issue:</span>{" "}
                                <span className="fw-bold text-dark">
                                  {statsLoading ? (
                                    <Skeleton.Button
                                      active
                                      size="small"
                                      style={{
                                        width: 24,
                                        height: 14,
                                        minWidth: 24,
                                      }}
                                    />
                                  ) : (
                                    stats.breakdown?.issue || 0
                                  )}
                                </span>
                              </div>
                              <div>
                                <span>Total Block:</span>{" "}
                                <span className="fw-bold text-dark">
                                  {statsLoading ? (
                                    <Skeleton.Button
                                      active
                                      size="small"
                                      style={{
                                        width: 24,
                                        height: 14,
                                        minWidth: 24,
                                      }}
                                    />
                                  ) : (
                                    stats.breakdown?.blocked || 0
                                  )}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          className={`card page-table-card page-table-device border-0 shadow rounded-4 mt-3 ${
            !isMobile ? "flex-fill overflow-y-auto" : ""
          }`}
        >
          <div
            className={`card-body ${!isMobile ? "overflow-y-auto" : ""}`}
            id={scrollableDivId}
            ref={scrollableDivRef}
          >
            {statusFilter === "issue/block" ? (
              <IssueBlockTable />
            ) : isLoading ? (
              <FullScreenLoader />
            ) : (
              <InfiniteScroll
                dataLength={quoteRequests.length}
                next={handleLoadMore}
                hasMore={!!hasNextPage}
                loader={
                  isFetchingNextPage && (
                    <div className="text-center p-2">
                      <Spin tip="Loading..." />
                    </div>
                  )
                }
                scrollableTarget={!isMobile ? scrollableDivId : undefined}
                height={!isMobile ? "100%" : undefined}
                style={
                  !isMobile
                    ? { overflow: "auto" }
                    : { overflow: "visible", paddingBottom: 16 }
                }
              >
                {!isMobile ? (
                  <Table
                    dataSource={quoteRequests}
                    scroll={{ x: "max-content" }}
                    pagination={false}
                    columns={columns}
                    onChange={handleTableChange}
                    rowKey="_id"
                    bordered
                    className="custom-table"
                    onRow={(record: any) => ({
                      onClick: () => {
                        saveScrollPosition();
                        navigate(`/quote-requests/${record._id}`);
                      },
                      style: { cursor: "pointer" },
                    })}
                  />
                ) : (
                  /* --- MOBILE VIEW: Card elements only --- */
                  <div className="px-3">
                    <div className="row gx-3 gy-3">
                      {quoteRequests.map((record) => (
                        <div className="col-md-6" key={record._id}>
                          <div
                            className="card mobile-table-card mobile-font-s rounded-4 p-2 cursor-pointer"
                            onClick={() => {
                              saveScrollPosition();
                              navigate(`/quote-requests/${record._id}`);
                            }}
                            // style={{
                            //   borderLeft: "5px solid #1890ff",
                            // }}
                          >
                            {/* Card Header */}
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="fw-bold fs-5 text-primary">
                                {record.display_quote_request_id || "-"}
                              </span>
                              <div className="d-flex align-items-center gap-2">
                                {/* Status Badge */}
                                {(() => {
                                  const foundOpt = STATUS_OPTIONS.find(
                                    (s) => s.value === record.status,
                                  );
                                  const classNames =
                                    foundOpt?.classNames || "status-default";
                                  const label =
                                    foundOpt?.label || record.status;
                                  return (
                                    <span
                                      className={`billStatus ${classNames} d-inline-flex align-items-center`}
                                    >
                                      {svg.dotIcon}
                                      {label}
                                    </span>
                                  );
                                })()}

                                {/* More Options Dropdown */}
                                <div onClick={(e) => e.stopPropagation()}>
                                  <Dropdown
                                    overlay={() => MoreOptionsMenu(record)}
                                    trigger={["click"]}
                                    placement="bottomRight"
                                  >
                                    <span className="cursor-pointer">
                                      {svg?.tableMobileOption}
                                    </span>
                                  </Dropdown>
                                </div>
                              </div>
                            </div>

                            {/* Client Snapshot Company Name / Client Name */}
                            <h6 className="fw-bold fs-5 mb-1">
                              {record.customer_snapshot?.first_name ||
                              record.customer_snapshot?.last_name
                                ? `${
                                    record.customer_snapshot?.first_name || ""
                                  } ${
                                    record.customer_snapshot?.last_name || ""
                                  }`.trim()
                                : "-"}
                              {record.customer_snapshot?.company_name && (
                                <span className="text-secondary d-block small fw-normal mt-1">
                                  {record.customer_snapshot.company_name}
                                </span>
                              )}
                            </h6>

                            {/* Details Grid */}
                            <div className="mobile-card-details fs-6 fs-6-mob">
                              <div className="row gy-0">
                                {[
                                  {
                                    label: "Assigned",
                                    value: (() => {
                                      const members =
                                        record.assigned_team_members;
                                      if (!members || members.length === 0)
                                        return "Unassigned";
                                      return members
                                        .map((m: any) => getDecryptedName(m))
                                        .join(", ");
                                    })(),
                                  },
                                  {
                                    label: "Time Active",
                                    value: (() => {
                                      const selChange = record.selected_change;
                                      if (
                                        selChange &&
                                        (selChange.time_active ||
                                          selChange.active_time_formatted ||
                                          selChange.active_time_ms !==
                                            undefined)
                                      ) {
                                        return (
                                          selChange.time_active ||
                                          selChange.active_time_formatted ||
                                          getDurationString(
                                            selChange.active_time_ms,
                                          )
                                        );
                                      }
                                      const start = new Date(
                                        record.created_at,
                                      ).getTime();
                                      const end = record.design_approved_at
                                        ? new Date(
                                            record.design_approved_at,
                                          ).getTime()
                                        : new Date().getTime();
                                      const totalMs = Math.max(0, end - start);
                                      return getDurationString(totalMs);
                                    })(),
                                  },
                                  {
                                    label: "Issues",
                                    value: (() => {
                                      const issuesList =
                                        record?.selected_change?.issues ||
                                        record?.issues ||
                                        [];
                                      const activeIssues = issuesList.filter(
                                        (i: any) =>
                                          !i.is_deleted &&
                                          i.status !== "Resolved",
                                      ).length;
                                      return activeIssues > 0 ? (
                                        <Badge
                                          count={activeIssues}
                                          style={{
                                            backgroundColor: "#fff0f0",
                                            color: "#ff0004",
                                          }}
                                        />
                                      ) : (
                                        "-"
                                      );
                                    })(),
                                  },
                                  {
                                    label: "Created on",
                                    value: record.created_at
                                      ? formatToLocalDate(
                                          record.created_at,
                                          true,
                                        )
                                      : "-",
                                  },
                                ].map((item, idx) => (
                                  <div
                                    className="col-12 d-flex justify-content-between align-items-center"
                                    style={{
                                      paddingTop: "2px",
                                      paddingBottom: "2px",
                                    }}
                                    key={idx}
                                  >
                                    <span className="text-muted">
                                      {item.label}
                                    </span>
                                    <span className="fw-semibold text-end">
                                      {item.value}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </InfiniteScroll>
            )}
          </div>
        </div>
      </>

      {/* Details Request Drawer */}
      <Drawer
        title={
          selectedRequest
            ? `Request Details: ${selectedRequest.display_quote_request_id}`
            : "Request Details"
        }
        width={900}
        open={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        destroyOnClose
      >
        {detailsLoading || !selectedRequest ? (
          <div className="d-flex justify-content-center py-5">
            <Spin size="large" tip="Loading Request Details..." />
          </div>
        ) : (
          <div className="d-flex flex-column h-100">
            {/* Header / Summary stats */}
            <div className="bg-light p-3 rounded mb-4 border d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div>
                <span className="small text-muted d-block">
                  Client Snapshot
                </span>
                <span className="fw-bold fs-5 text-dark">
                  {selectedRequest.customer_snapshot?.first_name}{" "}
                  {selectedRequest.customer_snapshot?.last_name}
                </span>
                <span className="text-secondary d-block small">
                  {selectedRequest.customer_snapshot?.company_name || "N/A"}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="small text-muted me-1">Status:</span>
                <Select
                  value={selectedRequest.status}
                  style={{ width: "180px" }}
                  onChange={handleChangeStatus}
                  size="large"
                >
                  {STATUS_OPTIONS.filter(
                    (opt) => !["Quote Created", "Approved"].includes(opt.value),
                  ).map((opt) => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div>
                {selectedRequest.converted_quote_id ? (
                  <Button
                    type="primary"
                    style={{
                      backgroundColor: "#52c41a",
                      borderColor: "#52c41a",
                      color: "#fff",
                    }}
                    icon={<CheckOutlined />}
                    onClick={() => {
                      const quoteId =
                        selectedRequest.converted_quote_id?._id ||
                        selectedRequest.converted_quote_id;
                      if (quoteId) navigate(`/quotations/${quoteId}`);
                    }}
                  >
                    VIEW CONVERTED QUOTE
                  </Button>
                ) : (
                  ["admin", "sub-admin"].includes(userRole) &&
                  (selectedRequest.status === "Completed" ||
                    selectedRequest.status === "Approved") && (
                    <Button
                      type="primary"
                      icon={<ArrowRightOutlined />}
                      loading={convertingRequest}
                      onClick={handleConvertToQuote}
                      style={{
                        backgroundColor: "#722ed1",
                        borderColor: "#722ed1",
                      }}
                    >
                      CONVERT TO QUOTE
                    </Button>
                  )
                )}
              </div>
            </div>

            {/* Navigation Tabs */}
            <Tabs
              activeKey={activeDetailsTab}
              onChange={setActiveDetailsTab}
              className="mb-4"
            >
              <Tabs.TabPane tab="Overview" key="overview" />
              <Tabs.TabPane
                tab={`Client Changes (${
                  (selectedRequest.client_changes || []).filter(
                    (c: any) => !c.is_deleted,
                  ).length
                })`}
                key="client_changes"
              />
              <Tabs.TabPane
                tab={`Issues/Blocks (${
                  (
                    selectedRequest?.selected_change?.issues ||
                    selectedRequest?.issues ||
                    []
                  ).filter((i: any) => !i.is_deleted).length
                })`}
                key="issues"
              />
              <Tabs.TabPane
                tab={`Designs (${(selectedRequest.designs || []).length})`}
                key="designs"
              />
              <Tabs.TabPane tab="Status Logs & History" key="history" />
            </Tabs>

            {/* Tab Contents */}
            <div className="flex-fill overflow-y-auto pr-2">
              {activeDetailsTab === "overview" && (
                <div className="d-flex flex-column gap-4">
                  {/* General details */}
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Card
                        title="Client Details"
                        size="small"
                        bordered
                        className="h-100 shadow-sm"
                      >
                        <p className="mb-2">
                          <strong>Email:</strong>{" "}
                          {selectedRequest.customer_snapshot?.email || "-"}
                        </p>
                        <p className="mb-2">
                          <strong>Phone:</strong>{" "}
                          {selectedRequest.customer_snapshot?.phone || "-"}
                        </p>
                        <p className="mb-2">
                          <strong>VAT ID:</strong>{" "}
                          {selectedRequest.customer_snapshot?.vat_number || "-"}
                        </p>
                        <p className="mb-2">
                          <strong>Discount Applied:</strong>{" "}
                          {selectedRequest.customer_snapshot?.discount || 0}%
                        </p>
                        <p className="mb-0">
                          <strong>Markup Applied:</strong>{" "}
                          {selectedRequest.customer_snapshot?.margin || 0}%
                        </p>
                      </Card>
                    </div>
                    <div className="col-md-6">
                      <Card
                        title="Assignment Details"
                        size="small"
                        bordered
                        className="h-100 shadow-sm"
                      >
                        <p className="mb-2">
                          <strong>Assigned Design Members:</strong>
                        </p>
                        <div className="d-flex flex-wrap gap-2">
                          {selectedRequest.assigned_team_members?.length > 0 ? (
                            selectedRequest.assigned_team_members.map(
                              (m: any) => {
                                const name = getDecryptedName(m);
                                return (
                                  <Badge
                                    key={m._id}
                                    count={name}
                                    style={{
                                      backgroundColor: "#e6f7ff",
                                      color: "#1890ff",
                                      border: "1px solid #91d5ff",
                                    }}
                                  />
                                );
                              },
                            )
                          ) : (
                            <span className="text-muted">
                              No design members assigned.
                            </span>
                          )}
                        </div>
                        <p className="mt-3 mb-0">
                          <strong>Created By:</strong>{" "}
                          {getDecryptedName(selectedRequest.created_by)}
                        </p>
                      </Card>
                    </div>
                  </div>

                  {/* CKEditor Rendered Description */}
                  <Card
                    title="Request Description"
                    size="small"
                    bordered
                    className="shadow-sm"
                  >
                    <div
                      className="description-content p-2 bg-light border rounded"
                      style={{ minHeight: "150px" }}
                      dangerouslySetInnerHTML={{
                        __html:
                          selectedRequest.description ||
                          "<i>No description provided.</i>",
                      }}
                    />
                  </Card>
                </div>
              )}

              {activeDetailsTab === "client_changes" && (
                <div className="d-flex flex-column gap-3">
                  <div className="border p-3 rounded bg-light">
                    <h5 className="fw-semibold mb-2">Log Client Change</h5>
                    <Input.TextArea
                      className="form-control form-control-lg"
                      rows={3}
                      placeholder="Type modifications or requests sent by client..."
                      value={newClientChangeText}
                      onChange={(e) => setNewClientChangeText(e.target.value)}
                    />
                    <div className="d-flex justify-content-end mt-2">
                      <Button
                        type="primary"
                        onClick={handleAddClientChange}
                        disabled={!newClientChangeText.trim()}
                      >
                        LOG CHANGE
                      </Button>
                    </div>
                  </div>

                  <h5 className="fw-semibold mb-2">
                    History Logs (
                    {
                      selectedRequest.client_changes?.filter(
                        (c: any) => !c.is_deleted,
                      ).length
                    }
                    )
                  </h5>
                  <div className="d-flex flex-column gap-3">
                    {selectedRequest.client_changes
                      ?.filter((c: any) => !c.is_deleted)
                      .map((change: any) => (
                        <Card
                          key={change._id}
                          size="small"
                          className="shadow-sm"
                        >
                          {editingClientChangeId === change._id ? (
                            <div className="d-flex flex-column gap-2">
                              <Input.TextArea
                                className="form-control form-control-lg"
                                rows={2}
                                value={editingClientChangeText}
                                onChange={(e) =>
                                  setEditingClientChangeText(e.target.value)
                                }
                              />
                              <div className="d-flex justify-content-end gap-2">
                                <Button
                                  size="small"
                                  onClick={() => setEditingClientChangeId(null)}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="small"
                                  type="primary"
                                  onClick={() =>
                                    handleSaveClientChange(change._id)
                                  }
                                >
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div
                                className="fw-bold text-primary mb-1"
                                style={{ fontSize: "14px" }}
                              >
                                Change #
                                {change.change_no !== undefined &&
                                change.change_no !== null
                                  ? change.change_no
                                  : "-"}
                              </div>
                              <p className="mb-2 text-dark fs-6">
                                {change.content}
                              </p>
                              <div className="d-flex justify-content-between align-items-center text-muted small">
                                <span>
                                  By: {change.created_by?.first_name}{" "}
                                  {change.created_by?.last_name} |{" "}
                                  {formatToLocalDate(change.created_at, true)}
                                </span>
                                <div className="d-flex gap-2">
                                  <Button
                                    type="link"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() =>
                                      handleStartEditClientChange(change)
                                    }
                                  />
                                  <Button
                                    type="link"
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() =>
                                      handleDeleteClientChange(change._id)
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </Card>
                      ))}
                  </div>
                </div>
              )}

              {activeDetailsTab === "issues" && (
                <div className="d-flex flex-column gap-3">
                  <div className="border p-3 rounded bg-light">
                    <h5 className="fw-semibold mb-2">Add Issue / Block</h5>
                    <div className="d-flex flex-column gap-2">
                      <Input
                        placeholder="Issue title or quick summary (e.g. Missing site specifications)"
                        value={newIssueContent}
                        onChange={(e) => setNewIssueContent(e.target.value)}
                      />
                      <Input.TextArea
                        className="form-control form-control-lg"
                        rows={2}
                        placeholder="Detailed description of issue / block..."
                        value={newIssueDescription}
                        onChange={(e) => setNewIssueDescription(e.target.value)}
                      />
                      <div className="d-flex justify-content-end">
                        <Button
                          type="primary"
                          onClick={handleAddIssue}
                          disabled={!newIssueContent.trim()}
                        >
                          ADD ISSUE
                        </Button>
                      </div>
                    </div>
                  </div>

                  <h5 className="fw-semibold mb-2">
                    Open Issues (
                    {
                      (
                        selectedRequest?.selected_change?.issues ||
                        selectedRequest?.issues ||
                        []
                      ).filter((i: any) => !i.is_deleted).length
                    }
                    )
                  </h5>
                  <div className="d-flex flex-column gap-3">
                    {(
                      selectedRequest?.selected_change?.issues ||
                      selectedRequest?.issues ||
                      []
                    )
                      .filter((i: any) => !i.is_deleted)
                      .map((issue: any) => (
                        <Card
                          key={issue._id}
                          size="small"
                          className="shadow-sm border-danger-left"
                        >
                          {editingIssueId === issue._id ? (
                            <div className="d-flex flex-column gap-2">
                              <Input
                                value={editingIssueContent}
                                onChange={(e) =>
                                  setEditingIssueContent(e.target.value)
                                }
                              />
                              <Input.TextArea
                                className="form-control form-control-lg"
                                rows={2}
                                value={editingIssueDescription}
                                onChange={(e) =>
                                  setEditingIssueDescription(e.target.value)
                                }
                              />
                              <div className="d-flex justify-content-end gap-2">
                                <Button
                                  size="small"
                                  onClick={() => setEditingIssueId(null)}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="small"
                                  type="primary"
                                  onClick={() => handleSaveIssue(issue._id)}
                                >
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <h6 className="fw-bold text-danger mb-1">
                                {issue.content}
                              </h6>
                              {issue.description && (
                                <p className="mb-2 text-secondary">
                                  {issue.description}
                                </p>
                              )}
                              <div className="d-flex justify-content-between align-items-center text-muted small">
                                <span>
                                  By: {issue.created_by?.first_name}{" "}
                                  {issue.created_by?.last_name} |{" "}
                                  {formatToLocalDate(issue.created_at, true)}
                                </span>
                                <div className="d-flex gap-2">
                                  <Button
                                    type="link"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => handleStartEditIssue(issue)}
                                  />
                                  <Button
                                    type="link"
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleDeleteIssue(issue._id)}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </Card>
                      ))}
                  </div>
                </div>
              )}

              {activeDetailsTab === "designs" && (
                <div className="d-flex flex-column gap-3">
                  <div className="border p-3 rounded bg-light d-flex justify-content-between align-items-center">
                    <div>
                      <h5 className="fw-semibold mb-1">
                        Upload New Design File
                      </h5>
                      <span className="small text-muted">
                        Supports images, PDF or design source files
                      </span>
                    </div>
                    <div>
                      <input
                        type="file"
                        id="design-file-input"
                        className="d-none"
                        onChange={handleDesignUpload}
                      />
                      <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        loading={uploadingDesign}
                        onClick={() =>
                          document.getElementById("design-file-input")?.click()
                        }
                      >
                        UPLOAD FILE
                      </Button>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <h5 className="fw-semibold mb-0">
                      Designs Uploaded ({selectedRequest.designs?.length || 0})
                    </h5>
                    {selectedRequest.designs?.length > 0 && (
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSendDesigns}
                      >
                        SEND ALL TO CLIENT
                      </Button>
                    )}
                  </div>

                  <div className="d-flex flex-column gap-2">
                    {selectedRequest.designs?.map((design: any) => (
                      <Card key={design._id} size="small" className="shadow-sm">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center gap-2">
                            <PaperClipOutlined className="text-primary" />
                            <div>
                              <a
                                href={design.file_url}
                                target="_blank"
                                rel="noreferrer"
                                className="fw-semibold text-primary"
                              >
                                {design.file_name}
                              </a>
                              <span className="d-block small text-muted">
                                Uploaded on:{" "}
                                {formatToLocalDate(design.uploaded_at, true)}
                              </span>
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-3">
                            {design.is_sent_to_client ? (
                              <span className="text-success small fw-semibold">
                                <CheckOutlined /> Sent to client
                              </span>
                            ) : (
                              <span className="text-muted small">Not sent</span>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {activeDetailsTab === "history" && (
                <div className="d-flex flex-column gap-4">
                  {/* Status Timeline */}
                  <div>
                    <h5 className="fw-semibold mb-3">Status Logs Timeline</h5>
                    <Timeline mode="left">
                      {selectedRequest.status_logs?.map(
                        (log: any, idx: number) => {
                          const isCurrent = !log.ended_at;
                          return (
                            <Timeline.Item
                              key={idx}
                              color={isCurrent ? "blue" : "gray"}
                              dot={
                                isCurrent ? (
                                  <ClockCircleOutlined />
                                ) : (
                                  <CheckCircleOutlined />
                                )
                              }
                            >
                              <div className="fw-bold">{log.status}</div>
                              <div className="small text-muted">
                                Started:{" "}
                                {formatToLocalDate(log.started_at, true)}
                              </div>
                              {log.ended_at && (
                                <div className="small text-muted">
                                  Ended: {formatToLocalDate(log.ended_at, true)}{" "}
                                  (Duration:{" "}
                                  {getDurationString(log.duration_ms || 0)})
                                </div>
                              )}
                              {isCurrent && (
                                <div className="small text-primary fw-bold mt-1">
                                  Current Active Stage
                                </div>
                              )}
                            </Timeline.Item>
                          );
                        },
                      )}
                    </Timeline>
                  </div>

                  {/* Audit Logs */}
                  <div>
                    <h5 className="fw-semibold mb-3">Audit Logs History</h5>
                    <div
                      className="d-flex flex-column gap-2"
                      style={{ maxHeight: "300px", overflowY: "auto" }}
                    >
                      {selectedRequest.history_logs?.map(
                        (log: any, idx: number) => (
                          <div
                            key={idx}
                            className="border-bottom py-2 d-flex justify-content-between align-items-start gap-3"
                          >
                            <div>
                              <span className="fw-semibold text-dark d-block">
                                {log.action}
                              </span>
                              {log.details && (
                                <span className="small text-muted">
                                  {JSON.stringify(log.details)}
                                </span>
                              )}
                            </div>
                            <span className="small text-secondary">
                              {log.performed_by?.first_name}{" "}
                              {log.performed_by?.last_name} |{" "}
                              {formatToLocalDate(log.performed_at, true)}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Drawer>
      <CommonConfirmationModal
        open={!!deleteRequestId}
        onClose={() => setDeleteRequestId(null)}
        title="Remove Design Request, Are You Sure?"
        loading={deleteRequestLoading}
        onSuccess={handleDeleteQuoteRequest}
        subTitle="This action will permanently delete this design request and its attachments."
      />

      <CommonConfirmationModal
        open={archiveModalOpen}
        loading={archiveLoading}
        title={
          selectedArchiveRequest?.is_archived
            ? "Unarchive Design Request?"
            : "Archive Design Request?"
        }
        subTitle={
          selectedArchiveRequest?.is_archived
            ? "Are you sure you want to unarchive this Design Request?"
            : "Are you sure you want to archive this Design Request?"
        }
        onClose={() => {
          setArchiveModalOpen(false);
          setSelectedArchiveRequest(null);
        }}
        onSuccess={handleToggleArchive}
        confirmButtonText="Confirm"
      />

      <CommonConfirmationModal
        open={confirmConvertOpen}
        loading={convertingRequest}
        title="Convert to Quote"
        subTitle={`Are you sure you want to convert Design Request ${selectedRequest?.display_quote_request_id} to a Quotation?`}
        onClose={() => setConfirmConvertOpen(false)}
        onSuccess={handleConfirmConvert}
        confirmButtonText="CREATE"
        confirmButtonColorClass="btn-dark"
      />

      <CommonConfirmationModal
        open={unresolvedWarningOpen}
        title="Unresolved Issues / Blocks Found"
        subTitle={unresolvedWarningText}
        onClose={() => setUnresolvedWarningOpen(false)}
        onSuccess={() => setUnresolvedWarningOpen(false)}
        onlyCloseButton={true}
      />
    </div>
  );
}
